<template>
  <div class="users-container">
    <!-- Cabeçalho -->
    <div class="header-section">
      <v-container fluid class="pa-4">
        <v-card class="modern-card" elevation="8">
          <v-card-title class="modern-card-title d-flex align-center justify-space-between">
            <div class="d-flex align-center">
              <v-icon left large color="primary">mdi-account-group</v-icon>
              <span class="text-h5 ml-2">Gestão de Usuários</span>
            </div>
            <v-btn 
              color="primary" 
              @click="openCreateDialog"
              class="ml-4"
            >
              <v-icon left>mdi-account-plus</v-icon>
              Adicionar Usuário
            </v-btn>
          </v-card-title>
        </v-card>
      </v-container>
    </div>

    <!-- Tabela de usuários -->
    <div class="content-section">
      <v-container fluid class="pa-4">
        <v-card class="modern-card" elevation="8">
          <v-data-table
            :headers="headers"
            :items="users"
            :loading="loading"
            :search="search"
            :items-per-page="15"
            :items-per-page-options="[10, 15, 25, 50]"
            sort-by="username"
            :sort-desc="false"
            class="modern-table"
            :class="{ 'table-dark': $vuetify.theme.dark }"
          >
            <!-- Coluna ID -->
            <template v-slot:item.id="{ item }">
              <v-chip small outlined color="primary" class="font-weight-medium">
                {{ item.id }}
              </v-chip>
            </template>

            <!-- Coluna Nome -->
            <template v-slot:item.username="{ item }">
              <span class="font-weight-medium">{{ item.username }}</span>
            </template>

            <!-- Coluna E-mail -->
            <template v-slot:item.email="{ item }">
              <span>{{ item.email || 'N/A' }}</span>
            </template>

            <!-- Coluna Cargo (Role) -->
            <template v-slot:item.role="{ item }">
              <v-chip 
                small 
                :color="getRoleColor(item.role)"
                text-color="white"
                class="font-weight-medium"
              >
                <v-icon left small>{{ getRoleIcon(item.role) }}</v-icon>
                {{ translateRole(item.role) }}
              </v-chip>
            </template>

            <!-- Coluna Status -->
            <template v-slot:item.isActive="{ item }">
              <v-chip 
                small 
                :color="item.isActive ? 'success' : 'error'"
                text-color="white"
                class="font-weight-medium"
              >
                <v-icon left small>{{ item.isActive ? 'mdi-check-circle' : 'mdi-close-circle' }}</v-icon>
                {{ item.isActive ? 'Ativo' : 'Inativo' }}
              </v-chip>
            </template>

            <!-- Ações -->
            <template v-slot:item.actions="{ item }">
              <v-btn 
                small 
                color="primary" 
                @click="openEditDialog(item)"
                class="mr-2"
              >
                <v-icon small>mdi-pencil</v-icon>
              </v-btn>
            </template>
          </v-data-table>
        </v-card>
      </v-container>
    </div>

    <!-- Dialog para criar/editar usuário -->
    <v-dialog v-model="userDialog" max-width="600px" persistent>
      <v-card class="modern-dialog-card">
        <v-card-title class="d-flex align-center">
          <v-icon left color="primary">{{ isEditing ? 'mdi-pencil' : 'mdi-account-plus' }}</v-icon>
          <span>{{ isEditing ? 'Editar Usuário' : 'Adicionar Usuário' }}</span>
          <v-spacer></v-spacer>
          <v-btn icon @click="closeDialog">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        
        <v-card-text class="px-6 py-4">
          <v-form ref="userForm" v-model="valid">
            <v-text-field
              v-model="userForm.username"
              label="Nome de Usuário"
              :rules="[rules.required]"
              outlined
              dense
              class="mb-3"
              :disabled="isEditing"
            ></v-text-field>

            <v-text-field
              v-model="userForm.email"
              label="E-mail"
              :rules="[rules.email]"
              outlined
              dense
              class="mb-3"
              type="email"
            ></v-text-field>

            <v-text-field
              v-model="userForm.password"
              :label="isEditing ? 'Nova Senha (deixe em branco para não alterar)' : 'Senha'"
              :rules="isEditing ? [] : [rules.required, rules.minLength]"
              outlined
              dense
              type="password"
              class="mb-3"
            ></v-text-field>

            <v-select
              v-model="userForm.role"
              :items="roleOptions"
              label="Cargo"
              :rules="[rules.required]"
              outlined
              dense
              class="mb-3"
            >
              <template v-slot:item="{ item }">
                <v-list-item-content>
                  <v-list-item-title>
                    <v-icon small left :color="getRoleColor(item.value)">
                      {{ getRoleIcon(item.value) }}
                    </v-icon>
                    {{ item.text }}
                  </v-list-item-title>
                </v-list-item-content>
              </template>
            </v-select>

            <v-switch
              v-model="userForm.isActive"
              label="Usuário Ativo"
              color="success"
              hide-details
              class="mt-2"
            ></v-switch>
          </v-form>
        </v-card-text>

        <v-card-actions class="px-6 py-4">
          <v-spacer></v-spacer>
          <v-btn 
            text 
            @click="closeDialog"
            class="mr-2"
          >
            Cancelar
          </v-btn>
          <v-btn 
            color="primary" 
            @click="saveUser"
            :loading="saving"
            :disabled="!valid"
          >
            <v-icon left>{{ isEditing ? 'mdi-content-save' : 'mdi-check' }}</v-icon>
            {{ isEditing ? 'Salvar' : 'Criar' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Snackbar para mensagens -->
    <v-snackbar v-model="snackbar.show" :color="snackbar.color" :timeout="5000">
      {{ snackbar.text }}
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snackbar.show = false">
          Fechar
        </v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import usersService from '../services/usersService'

export default {
  name: 'UsersView',
  data() {
    return {
      loading: false,
      saving: false,
      search: '',
      users: [],
      userDialog: false,
      isEditing: false,
      valid: false,
      userForm: {
        id: null,
        username: '',
        email: '',
        password: '',
        role: 'user',
        isActive: true
      },
      rules: {
        required: v => !!v || 'Campo obrigatório',
        email: v => !v || /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/.test(v) || 'E-mail inválido',
        minLength: v => !v || v.length >= 6 || 'A senha deve ter no mínimo 6 caracteres'
      },
      roleOptions: [
        { text: 'Administrador', value: 'admin' },
        { text: 'Gestor', value: 'gestor' },
        { text: 'Usuário', value: 'user' }
      ],
      headers: [
        {
          text: 'ID',
          align: 'start',
          sortable: true,
          value: 'id',
          width: '80px'
        },
        {
          text: 'Nome',
          align: 'start',
          sortable: true,
          value: 'username'
        },
        {
          text: 'E-mail',
          align: 'start',
          sortable: true,
          value: 'email'
        },
        {
          text: 'Cargo',
          align: 'start',
          sortable: true,
          value: 'role',
          width: '150px'
        },
        {
          text: 'Status',
          align: 'start',
          sortable: true,
          value: 'isActive',
          width: '130px'
        },
        {
          text: 'Ações',
          align: 'center',
          sortable: false,
          value: 'actions',
          width: '100px'
        }
      ],
      snackbar: {
        show: false,
        text: '',
        color: 'info'
      }
    }
  },
  
  mounted() {
    this.loadUsers()
  },
  
  methods: {
    /**
     * Carregar lista de usuários
     */
    async loadUsers() {
      this.loading = true
      try {
        const response = await usersService.listUsers()
        if (response.success) {
          this.users = response.users || []
        } else {
          this.showSnackbar('Erro ao carregar usuários', 'error')
        }
      } catch (error) {
        console.error('Erro ao carregar usuários:', error)
        this.showSnackbar(
          error.response?.data?.message || 'Erro ao carregar usuários',
          'error'
        )
      } finally {
        this.loading = false
      }
    },
    
    /**
     * Abrir dialog para criar usuário
     */
    openCreateDialog() {
      this.isEditing = false
      this.userForm = {
        id: null,
        username: '',
        email: '',
        password: '',
        role: 'user',
        isActive: true
      }
      this.userDialog = true
      this.$nextTick(() => {
        if (this.$refs.userForm) {
          this.$refs.userForm.resetValidation()
        }
      })
    },
    
    /**
     * Abrir dialog para editar usuário
     */
    openEditDialog(user) {
      this.isEditing = true
      this.userForm = {
        id: user.id,
        username: user.username,
        email: user.email || '',
        password: '',
        role: user.role,
        isActive: user.isActive
      }
      this.userDialog = true
      this.$nextTick(() => {
        if (this.$refs.userForm) {
          this.$refs.userForm.resetValidation()
        }
      })
    },
    
    /**
     * Fechar dialog
     */
    closeDialog() {
      this.userDialog = false
      this.userForm = {
        id: null,
        username: '',
        email: '',
        password: '',
        role: 'user',
        isActive: true
      }
      if (this.$refs.userForm) {
        this.$refs.userForm.resetValidation()
      }
    },
    
    /**
     * Salvar usuário (criar ou atualizar)
     */
    async saveUser() {
      if (!this.$refs.userForm.validate()) {
        return
      }
      
      this.saving = true
      try {
        const userData = {
          username: this.userForm.username,
          email: this.userForm.email || null,
          role: this.userForm.role,
          isActive: this.userForm.isActive
        }
        
        // Adicionar senha apenas se foi informada (na criação é obrigatória)
        if (this.userForm.password) {
          userData.password = this.userForm.password
        } else if (!this.isEditing) {
          // Na criação, senha é obrigatória
          this.showSnackbar('A senha é obrigatória para novos usuários', 'error')
          this.saving = false
          return
        }
        
        let response
        if (this.isEditing) {
          response = await usersService.updateUser(this.userForm.id, userData)
        } else {
          response = await usersService.createUser(userData)
        }
        
        if (response.success) {
          this.showSnackbar(
            this.isEditing ? 'Usuário atualizado com sucesso' : 'Usuário criado com sucesso',
            'success'
          )
          this.closeDialog()
          await this.loadUsers()
        } else {
          this.showSnackbar(
            response.message || 'Erro ao salvar usuário',
            'error'
          )
        }
      } catch (error) {
        console.error('Erro ao salvar usuário:', error)
        this.showSnackbar(
          error.response?.data?.message || 'Erro ao salvar usuário',
          'error'
        )
      } finally {
        this.saving = false
      }
    },
    
    /**
     * Traduzir role
     */
    translateRole(role) {
      const translations = {
        'admin': 'Administrador',
        'gestor': 'Gestor',
        'user': 'Usuário'
      }
      return translations[role] || role
    },
    
    /**
     * Obter cor do chip baseado na role
     */
    getRoleColor(role) {
      const colors = {
        'admin': 'orange',
        'gestor': 'purple',
        'user': 'blue'
      }
      return colors[role] || 'grey'
    },
    
    /**
     * Obter ícone baseado na role
     */
    getRoleIcon(role) {
      const icons = {
        'admin': 'mdi-shield-crown',
        'gestor': 'mdi-account-tie',
        'user': 'mdi-account'
      }
      return icons[role] || 'mdi-account'
    },
    
    /**
     * Mostrar snackbar
     */
    showSnackbar(text, color = 'info') {
      this.snackbar.text = text
      this.snackbar.color = color
      this.snackbar.show = true
    }
  }
}
</script>

<style scoped>
.users-container {
  height: calc(100vh - 64px - 16px - 36px);
  max-height: calc(100vh - 64px - 16px - 36px);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-sizing: border-box;
}

.header-section {
  flex-shrink: 0;
  background: transparent;
  z-index: 10;
}

.content-section {
  flex: 1 1 auto;
  overflow: auto;
  min-height: 0;
}

/* Card moderno */
.modern-card {
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(255, 255, 255, 0.8) 100%);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.theme--dark .modern-card {
  background: linear-gradient(135deg, rgba(30, 30, 30, 0.9) 0%, rgba(45, 45, 45, 0.8) 100%);
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.modern-card-title {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);
  padding: 20px 24px !important;
}

.theme--dark .modern-card-title {
  background: linear-gradient(135deg, #2d2d2d 0%, #3d3d3d 100%);
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

/* Tabela moderna */
.modern-table {
  border-radius: 12px !important;
  overflow: hidden;
  background: transparent !important;
}

.modern-table .v-data-table__wrapper {
  border-radius: 12px;
}

/* Diálogo moderno */
.modern-dialog-card {
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(255, 255, 255, 0.9) 100%);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  box-shadow: 0 25px 50px rgba(0, 0, 0, 0.25) !important;
}

.theme--dark .modern-dialog-card {
  background: linear-gradient(135deg, rgba(30, 30, 30, 0.95) 0%, rgba(45, 45, 45, 0.9) 100%);
  border: 1px solid rgba(255, 255, 255, 0.1);
}
</style>
